import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  Clock,
  Users,
  Calendar,
  ExternalLink,
  Star,
  Award,
  BookOpen
} from 'lucide-react';
import { ResearchOpportunity } from '../../types';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';
import { Button } from '../shared/Button';

interface ResearchCardProps {
  opportunity: ResearchOpportunity;
  onApply: (opportunity: ResearchOpportunity) => void;
  onSave: (opportunity: ResearchOpportunity) => void;
}

export const ResearchCard: React.FC<ResearchCardProps> = ({
  opportunity,
  onApply,
  onSave
}) => {
  const getFieldColor = (field: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'accent' | 'success' | 'warning'> = {
      stem: 'primary',
      medicine: 'error' as any,
      engineering: 'secondary',
      'computer-science': 'primary',
      biology: 'success',
      chemistry: 'accent',
      physics: 'secondary',
      psychology: 'warning',
      'social-science': 'secondary',
      humanities: 'accent',
      environmental: 'success',
      other: 'secondary'
    };
    return colors[field] || 'secondary';
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'paid': return 'success';
      case 'academic-credit': return 'primary';
      case 'unpaid': return 'secondary';
      default: return 'secondary';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const daysUntilDeadline = Math.ceil(
    (opportunity.deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <Card hover className="group h-full flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <Badge variant={getFieldColor(opportunity.field)} size="sm">
              {opportunity.field.replace('-', ' ')}
            </Badge>
            <Badge variant={getTypeColor(opportunity.type)} size="sm">
              {opportunity.type.replace('-', ' ')}
            </Badge>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-2">
            {opportunity.title}
          </h3>
          <p className="text-sm text-gray-600 font-medium">
            {opportunity.institution}
          </p>
        </div>

        <div className="flex items-center bg-primary-50 px-2 py-1 rounded-lg">
          <Star className="w-4 h-4 text-primary-500 mr-1" />
          <span className="text-sm font-semibold text-primary-700">
            {opportunity.matchScore}%
          </span>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-600 text-sm mb-4 line-clamp-3 flex-1">
        {opportunity.description}
      </p>

      {/* Details Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div className="flex items-center text-gray-500">
          <MapPin size={14} className="mr-2" />
          <span className="truncate">{opportunity.location}</span>
        </div>
        <div className="flex items-center text-gray-500">
          <Clock size={14} className="mr-2" />
          <span>{opportunity.duration}</span>
        </div>
        <div className="flex items-center text-gray-500">
          <Users size={14} className="mr-2" />
          <span>{opportunity.ageRange.min}-{opportunity.ageRange.max} years</span>
        </div>
        <div className="flex items-center text-gray-500">
          <Calendar size={14} className="mr-2" />
          <span className={daysUntilDeadline <= 7 ? 'text-red-600 font-medium' : ''}>
            Due {formatDate(opportunity.deadline)}
          </span>
        </div>
      </div>

      {/* Requirements */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
          <BookOpen size={12} className="mr-1" />
          Requirements
        </h4>
        <div className="flex flex-wrap gap-1">
          {opportunity.requirements.slice(0, 3).map((req, index) => (
            <span
              key={index}
              className="inline-block px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded"
            >
              {req}
            </span>
          ))}
          {opportunity.requirements.length > 3 && (
            <span className="inline-block px-2 py-1 bg-gray-100 text-xs text-gray-600 rounded">
              +{opportunity.requirements.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Benefits */}
      <div className="mb-6">
        <h4 className="text-xs font-semibold text-gray-700 mb-2 flex items-center">
          <Award size={12} className="mr-1" />
          Benefits
        </h4>
        <ul className="space-y-1">
          {opportunity.benefits.slice(0, 2).map((benefit, index) => (
            <li key={index} className="text-xs text-gray-600 flex items-start">
              <span className="w-1 h-1 bg-primary-400 rounded-full mt-1.5 mr-2 flex-shrink-0" />
              {benefit}
            </li>
          ))}
          {opportunity.benefits.length > 2 && (
            <li className="text-xs text-primary-600">
              +{opportunity.benefits.length - 2} more benefits
            </li>
          )}
        </ul>
      </div>

      {/* Actions */}
      <div className="flex space-x-2 mt-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onSave(opportunity)}
          className="flex-1"
        >
          Save
        </Button>
        <Button
          size="sm"
          onClick={() => onApply(opportunity)}
          className="flex-1 flex items-center justify-center space-x-1"
        >
          <ExternalLink size={14} />
          <span>Apply</span>
        </Button>
      </div>

      {/* Deadline Warning */}
      {daysUntilDeadline <= 7 && daysUntilDeadline > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg"
        >
          <p className="text-xs text-red-700 font-medium">
            ⚠️ Deadline in {daysUntilDeadline} day{daysUntilDeadline !== 1 ? 's' : ''}!
          </p>
        </motion.div>
      )}

      {daysUntilDeadline <= 0 && (
        <div className="mt-3 p-2 bg-gray-100 border border-gray-200 rounded-lg">
          <p className="text-xs text-gray-600">
            Deadline passed
          </p>
        </div>
      )}
    </Card>
  );
};